if (!customElements.get('editable-video-testimonials')) {
  class EditableVideoTestimonials extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.track = this.querySelector('[data-evt-track]');
      this.cards = [...this.querySelectorAll('[data-evt-card]')];
      this.dots = [...this.querySelectorAll('[data-evt-dot]')];
      this.dialog = this.querySelector('[data-evt-dialog]');
      this.dialogMedia = this.querySelector('[data-evt-dialog-media]');
      this.autoAdvanceSeconds = Number(this.dataset.autoAdvance || 0);
      this.autoTimer = 0;
      this.paginationFrame = 0;

      this.addEventListener('click', (event) => this.handleClick(event));
      this.track?.addEventListener('keydown', (event) => this.handleTrackKeys(event));
      this.track?.addEventListener('pointerdown', () => this.pauseAutoAdvance(), { passive: true });
      this.track?.addEventListener('scroll', () => this.queuePaginationUpdate(), { passive: true });
      this.addEventListener('mouseenter', () => this.pauseAutoAdvance());
      this.addEventListener('mouseleave', () => this.startAutoAdvance());
      this.addEventListener('focusin', () => this.pauseAutoAdvance());
      this.addEventListener('focusout', () => this.startAutoAdvance());
      this.dialog?.addEventListener('close', () => {
        this.clearDialog();
        this.startAutoAdvance();
      });
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.closeDialog();
      });
      this.addEventListener('shopify:block:select', (event) => {
        const card = event.target.closest('[data-evt-card]');
        if (card) this.scrollToCard(card, false);
      });

      this.queuePaginationUpdate();
      this.startAutoAdvance();
    }

    disconnectedCallback() {
      this.pauseAutoAdvance();
      window.cancelAnimationFrame(this.paginationFrame);
      this.stopAllVideos();
      this.clearDialog();
    }

    handleClick(event) {
      const playButton = event.target.closest('[data-evt-play]');
      if (playButton) {
        this.toggleInlineVideo(playButton.closest('[data-evt-card]'));
        return;
      }

      const expandButton = event.target.closest('[data-evt-expand]');
      if (expandButton) {
        this.openDialog(expandButton.closest('[data-evt-card]'));
        return;
      }

      const dot = event.target.closest('[data-evt-dot]');
      if (dot) {
        const card = this.orderedCards()[Number(dot.dataset.evtDot)];
        if (card) {
          this.scrollToCard(card);
          this.updatePagination(Number(dot.dataset.evtDot));
          this.restartAutoAdvance();
        }
        return;
      }

      if (event.target.closest('[data-evt-close]')) this.closeDialog();
    }

    toggleInlineVideo(card) {
      const video = card?.querySelector('.evt__video');
      const button = card?.querySelector('[data-evt-play]');
      if (!video || !button) return;

      if (!video.paused) {
        this.resetVideo(card);
        this.startAutoAdvance();
        return;
      }

      this.stopAllVideos(card);
      this.pauseAutoAdvance();
      video.muted = false;
      video.volume = 0.35;
      const playPromise = video.play();
      if (playPromise) {
        playPromise
          .then(() => {
            card.classList.add('is-playing');
            button.setAttribute('aria-pressed', 'true');
          })
          .catch(() => this.resetVideo(card));
      }
      video.addEventListener('ended', () => this.resetVideo(card), { once: true });
    }

    resetVideo(card) {
      const video = card?.querySelector('.evt__video');
      if (video) {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (_error) {
          // Metadata may not be available yet; pausing is still sufficient.
        }
      }
      card?.classList.remove('is-playing');
      card?.querySelector('[data-evt-play]')?.setAttribute('aria-pressed', 'false');
    }

    stopAllVideos(exceptCard) {
      this.cards.forEach((card) => {
        if (card !== exceptCard) this.resetVideo(card);
      });
    }

    openDialog(card) {
      const sourceVideo = card?.querySelector('.evt__video');
      if (!sourceVideo || !this.dialog || !this.dialogMedia) return;

      this.stopAllVideos();
      this.pauseAutoAdvance();
      const dialogVideo = sourceVideo.cloneNode(true);
      dialogVideo.removeAttribute('class');
      dialogVideo.controls = true;
      dialogVideo.autoplay = true;
      dialogVideo.muted = false;
      dialogVideo.volume = 0.5;
      this.dialogMedia.replaceChildren(dialogVideo);

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
        dialogVideo.play().catch(() => {});
      } else if (sourceVideo.requestFullscreen) {
        sourceVideo.requestFullscreen();
        sourceVideo.play().catch(() => {});
      }
    }

    closeDialog() {
      if (this.dialog?.open) this.dialog.close();
      this.clearDialog();
      this.startAutoAdvance();
    }

    clearDialog() {
      const video = this.dialogMedia?.querySelector('video');
      video?.pause();
      this.dialogMedia?.replaceChildren();
    }

    orderedCards() {
      if (!this.track) return [];
      return [...this.cards].sort((first, second) => first.offsetLeft - second.offsetLeft);
    }

    currentCardIndex() {
      const cards = this.orderedCards();
      if (!cards.length || !this.track) return 0;
      const trackLeft = this.track.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      return nearestIndex;
    }

    queuePaginationUpdate() {
      if (this.paginationFrame) return;
      this.paginationFrame = window.requestAnimationFrame(() => {
        this.paginationFrame = 0;
        this.updatePagination();
      });
    }

    updatePagination(activeIndex = this.currentCardIndex()) {
      this.dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    scrollByCard(direction) {
      const cards = this.orderedCards();
      if (!cards.length) return;
      const nextIndex = Math.max(0, Math.min(cards.length - 1, this.currentCardIndex() + direction));
      this.scrollToCard(cards[nextIndex]);
      this.restartAutoAdvance();
    }

    scrollToCard(card, smooth = true) {
      if (!card || !this.track) return;
      this.track.scrollTo({
        left: card.offsetLeft - this.track.offsetLeft,
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    }

    handleTrackKeys(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const cards = this.orderedCards();
      if (!cards.length) return;
      let nextIndex = this.currentCardIndex();
      if (event.key === 'ArrowLeft') nextIndex = Math.max(0, nextIndex - 1);
      if (event.key === 'ArrowRight') nextIndex = Math.min(cards.length - 1, nextIndex + 1);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = cards.length - 1;
      event.preventDefault();
      this.scrollToCard(cards[nextIndex]);
      this.restartAutoAdvance();
    }

    startAutoAdvance() {
      if (
        this.autoTimer ||
        this.autoAdvanceSeconds <= 0 ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        window.matchMedia('(min-width: 750px)').matches
      ) {
        return;
      }
      this.autoTimer = window.setInterval(() => {
        const cards = this.orderedCards();
        if (!cards.length) return;
        const nextIndex = (this.currentCardIndex() + 1) % cards.length;
        this.scrollToCard(cards[nextIndex]);
      }, this.autoAdvanceSeconds * 1000);
    }

    pauseAutoAdvance() {
      if (!this.autoTimer) return;
      window.clearInterval(this.autoTimer);
      this.autoTimer = 0;
    }

    restartAutoAdvance() {
      this.pauseAutoAdvance();
      this.startAutoAdvance();
    }
  }

  customElements.define('editable-video-testimonials', EditableVideoTestimonials);
}
